import { Text } from "@clipboard-health/ui-components";
import { Box, Stack, useTheme } from "@mui/material";
import { forwardRef, useCallback } from "react";

export interface ShiftRecommendationsOverlayProps {
  currentIndex: number;
  totalStories: number;
  onNext: () => void;
  onPrevious: () => void;
  currentProgress: number;
  onPause: () => void;
  onResume: () => void;
}

export const ShiftRecommendationsOverlay = forwardRef<
  HTMLDivElement,
  ShiftRecommendationsOverlayProps
>((props, ref) => {
  const { currentIndex, totalStories, onNext, onPrevious, currentProgress, onPause, onResume } =
    props;
  const theme = useTheme();

  const handleTapLeft = useCallback(() => {
    onPrevious();
  }, [onPrevious]);

  const handleTapRight = useCallback(() => {
    onNext();
  }, [onNext]);

  const handleCenterTouchStart = useCallback(() => {
    onPause();
  }, [onPause]);

  const handleCenterTouchEnd = useCallback(() => {
    onResume();
  }, [onResume]);

  return (
    <>
      {/* Progress Indicators */}
      <Box
        ref={ref}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10_000,
          p: 2,
          display: "flex",
          gap: 1,
          pointerEvents: "auto",
        }}
      >
        {Array.from({ length: totalStories }, (_, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: 3,
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              borderRadius: 1.5,
              overflow: "hidden",
              zIndex: 10_000,
            }}
          >
            <Box
              sx={{
                height: "100%",
                backgroundColor: theme.palette.common.white,
                width: (() => {
                  if (index < currentIndex) {
                    return "100%";
                  }

                  if (index === currentIndex) {
                    return `${currentProgress * 100}%`;
                  }

                  return "0%";
                })(),
                transition: index === currentIndex ? "width 0.1s linear" : "width 0.3s ease",
                borderRadius: 1.5,
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10_001,
          p: 2,
          pt: 5,
          // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
          background: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)",
          pointerEvents: "auto",
        }}
      />

      {/* Story Counter */}
      <Box
        sx={{
          position: "absolute",
          top: 80,
          left: 0,
          right: 0,
          zIndex: 10_001,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "auto",
        }}
      >
        <Text
          variant="body2"
          sx={{
            color: theme.palette.common.white,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            px: 2,
            py: 1,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
          }}
        >
          {currentIndex + 1} of {totalStories} shifts
        </Text>
      </Box>

      {/* Left tap zone - Previous */}
      <Box
        sx={{
          position: "absolute",
          top: 120,
          left: 0,
          width: "25%",
          height: "60%",
          zIndex: 10_001,
          pointerEvents: "auto",
        }}
        onClick={handleTapLeft}
      />

      {/* Center tap zone - Hold to pause */}
      <Box
        sx={{
          position: "absolute",
          top: 120,
          left: "25%",
          width: "50%",
          height: "60%",
          zIndex: 10_001,
          pointerEvents: "auto",
        }}
        onTouchStart={handleCenterTouchStart}
        onTouchEnd={handleCenterTouchEnd}
        onMouseDown={handleCenterTouchStart}
        onMouseUp={handleCenterTouchEnd}
      />

      {/* Right tap zone - Next */}
      <Box
        sx={{
          position: "absolute",
          top: 120,
          right: 0,
          width: "25%",
          height: "60%",
          zIndex: 10_001,
          pointerEvents: "auto",
        }}
        onClick={handleTapRight}
      />
    </>
  );
});

ShiftRecommendationsOverlay.displayName = "StoriesOverlay";
